window.Branch = window.Branch || {};
window.Branch.compositeRenderImage = window.Branch.compositeRenderImage || {};

// ---- Shared helpers (single source of truth, used by every composite function below) ----

// Normalize an option VALUE for comparison (e.g. "Woodgrain White" -> "woodgrain-white").
function normalizeValue(value){
    if (!value) return '';
    return value.toString().toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
}

// Normalize an option NAME for comparison/attribute use (e.g. "Top_Color" -> "top-color").
function normalizeOptionName(name){
    if (!name) return '';
    return name.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
}

// Look up and parse the composite merchandising data for a product id. Returns the parsed object, or null.
window.Branch.compositeRenderImage.getCompositeData = function(productId, contextEl){
    let el = null;
    // When a context element is supplied (e.g. a bundle/composite tile), prefer the
    // merchandising-data script that belongs to that tile. The same product can appear
    // multiple times on a page (its own standalone tile + as a constituent of bundle
    // tiles), and only the composite-slot tiles carry the bundle-builder layer images.
    // A global first-match would otherwise grab the standalone tile's script (no bundle layers).
    if (contextEl) {
        const scope = contextEl.closest('.product-tile') || contextEl.closest('.pre-set-bundle-image') || contextEl.parentElement;
        if (scope) {
            el = scope.querySelector(`.composite-render-image-merchandising-data[data-id="${productId}"]`);
        }
    }
    if (!el) el = document.querySelector(`.composite-render-image-merchandising-data[data-id="${productId}"]`);
    if (!el) return null;
    try {
        return JSON.parse(el.textContent);
    } catch (error) {
        console.error('Error parsing composite render image data:', error);
        return null;
    }
};

// Preference predicates used to pick the "best" image when a layer has several matches.
const PREFER_FRONT = (img) => img.dimensions_overlay_view === 'front';
const PREFER_OVERVIEW = (img) => img.user_groups && img.user_groups.includes('overview');

// Find the matching { first, second } composite layer images for a variant.
//   opts.layerKeys         = [firstLayerKey, secondLayerKey] to search (defaults to the regular tile layers)
//   opts.fallbackLayerKeys = optional [firstKey, secondKey] tried per-layer when a primary layer has no match
//   opts.prefer            = array of predicates tried in order to pick the best of multiple matches
// Returns null when either layer has no match.
window.Branch.compositeRenderImage.selectCompositeLayerPair = function(variant, productId, optionNames, opts){
    opts = opts || {};
    const layerKeys = opts.layerKeys || ['first_layer_images', 'second_layer_images'];
    const prefer = opts.prefer || [];
    const matchesFor = (key) => window.Branch.compositeRenderImage.returnRelevantCompositeImagesForVariant(variant, productId, [key], optionNames, opts.contextEl);
    let firstMatches = matchesFor(layerKeys[0]);
    let secondMatches = matchesFor(layerKeys[1]);
    if (opts.fallbackLayerKeys) {
        if (!firstMatches.length) firstMatches = matchesFor(opts.fallbackLayerKeys[0]);
        if (!secondMatches.length) secondMatches = matchesFor(opts.fallbackLayerKeys[1]);
    }
    if (!firstMatches.length || !secondMatches.length) return null;
    const best = (arr) => {
        for (const pred of prefer) { const match = arr.find(pred); if (match) return match; }
        return arr[0];
    };
    return { first: best(firstMatches), second: best(secondMatches) };
};

// Build the two stacked <img> tags (second layer behind, first layer in front) for a composite.
window.Branch.compositeRenderImage.buildCompositeLayersHTML = function(first, second, opts){
    opts = opts || {};
    const widths = opts.widths || [90, 180, 360, 540];
    const defaultWidth = opts.defaultWidth || 360;
    const loading = opts.loading || 'lazy';
    const extraClass = opts.extraClass ? ' ' + opts.extraClass : '';
    const altPrefix = opts.altPrefix || '';
    const style = opts.style || 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:contain;';
    const srcset = (url) => widths.map(w => `${url}&width=${w} ${w}w`).join(', ');
    return (
        `<img class="composite-layer-second${extraClass}" src="${second.url}&width=${defaultWidth}" srcset="${srcset(second.url)}" alt="${altPrefix}Second Layer" style="${style}" loading="${loading}">` +
        `<img class="composite-layer-first${extraClass}" src="${first.url}&width=${defaultWidth}" srcset="${srcset(first.url)}" alt="${altPrefix}First Layer" style="${style}" loading="${loading}">`
    );
};

window.Branch.compositeRenderImage.updateStaticTile = function(variant, productId, filterLayerKeys, productOptionNames, tile){
    if(!variant || !tile) return;
    let relevantCompositeImages = window.Branch.compositeRenderImage.returnRelevantCompositeImagesForVariant(variant, productId, filterLayerKeys, productOptionNames);
    if(relevantCompositeImages.length > 0){
        relevantCompositeImages = relevantCompositeImages.filter(img => img.dimensions_overlay_view === "front");
    }

    if(relevantCompositeImages.length  > 1){
        createInsertCompositeWrapperForProductTile(tile, relevantCompositeImages[0], relevantCompositeImages[1], variant, false, true, tile.getAttribute('data-lifestyle-as-main-image') == 'true')
    }
}

window.Branch.compositeRenderImage.returnRelevantCompositeImagesForVariant = function(variant, productId, filterLayerKeys, productOptionNames, contextEl){
    let results = [];
    if (!variant) return results;
    const compositeData = window.Branch.compositeRenderImage.getCompositeData(productId, contextEl);
    if (!compositeData) return results;

    const relevantOptions = [];
    if (compositeData.product_thumbs_group_by) {
        compositeData.product_thumbs_group_by.split(',').map(opt => opt.trim()).forEach(opt => {
            relevantOptions.push(normalizeOptionName(opt));
        });
    }

    // Build variant options map using options_names and options arrays.
    // productOptionNames wins when supplied — a variant serialized from Liquid
    // (`variant | json`) has no options_names, so the caller passes product.options instead.
    const variantOptions = {};
    let optionNames = productOptionNames || variant.options_names;
    if (optionNames && variant.options) {
        optionNames.forEach((optionName, index) => {
            const normalizedName = normalizeOptionName(optionName);
            if (relevantOptions.includes(normalizedName)) {
                const optionValue = variant.options[index];
                if (optionValue) {
                    variantOptions[normalizedName] = normalizeValue(optionValue);
                }
            }
        });
    }  else if (variant.featured_media && variant.featured_media.alt) {
        const altParts = variant.featured_media.alt.split(';');
        altParts.forEach(part => {
            const [key, value] = part.split(':').map(s => s.trim());
            if (key && value) {
                const normalizedKey = normalizeOptionName(key);
                if (relevantOptions.includes(normalizedKey)) {
                    variantOptions[normalizedKey] = normalizeValue(value);
                }
            }
        });
    }

    // Helper to check if an image matches the variant options
    const imageMatchesVariant = (image) => {
        for (const [imgOptionName, imgOptionValue] of Object.entries(image.options)) {
            const normalizedImgName = normalizeOptionName(imgOptionName);
            if (!relevantOptions.includes(normalizedImgName)) continue;
            if (variantOptions[normalizedImgName] !== normalizeValue(imgOptionValue)) return false;
        }
        return true;
    };

    // Check all layer types for matching images
    const layerKeys = filterLayerKeys || ['first_layer_images', 'second_layer_images', 'first_layer_bundle_builder_images', 'second_layer_bundle_builder_images'];
    layerKeys.forEach(key => {
        if (compositeData[key]) {
            compositeData[key].forEach(image => {
                if (imageMatchesVariant(image)) {
                    results.push(image);
                }
            });
        }
    });

    return results;
}

// Pick the { first, second } layer pair for a product used as a bundle constituent:
// prefer the bundle-builder layers, fall back to the regular tile layers as a whole pair.
// Shared by the DOM slot renderer below and by the recently-viewed cookie writer in
// jquery.products.js, so both resolve a constituent's composite the same way.
window.Branch.compositeRenderImage.selectConstituentLayerPair = function(variant, productId, productOptionNames, contextEl){
    const base = { prefer: [PREFER_FRONT, PREFER_OVERVIEW], contextEl: contextEl };
    const pick = (layerKeys) => window.Branch.compositeRenderImage.selectCompositeLayerPair(
        variant, productId, productOptionNames, Object.assign({ layerKeys: layerKeys }, base)
    );
    return pick(['first_layer_bundle_builder_images', 'second_layer_bundle_builder_images'])
        || pick(['first_layer_images', 'second_layer_images']);
};

window.Branch.compositeRenderImage.renderBundleConstituentComposite = function(variant, productId, productOptionNames, slotEl){
    if(!slotEl) return;
    const layers = window.Branch.compositeRenderImage.selectConstituentLayerPair(variant, productId, productOptionNames, slotEl);
    if(!layers) return;
    slotEl.innerHTML = window.Branch.compositeRenderImage.buildCompositeLayersHTML(layers.first, layers.second, { defaultWidth: 360 });
}

window.Branch.compositeRenderImage.renderProductComposite = function(variant, productId, productOptionNames, slotEl, opts){
    if(!slotEl) return false;
    const layers = window.Branch.compositeRenderImage.selectCompositeLayerPair(variant, productId, productOptionNames, {
        layerKeys: ['first_layer_images', 'second_layer_images'],
        prefer: [PREFER_FRONT, PREFER_OVERVIEW],
        contextEl: slotEl
    });
    if(!layers) return false;
    slotEl.innerHTML = window.Branch.compositeRenderImage.buildCompositeLayersHTML(layers.first, layers.second, Object.assign({ defaultWidth: 360 }, opts || {}));
    return true;
};

window.Branch.compositeRenderImage.initRenderImagesForBundleBuilder = function(variant, mainImageContainer, useBundleBuilderLayers = true){
    let compositeImageReplacementFound = false;
// console.log('initRenderImagesForBundleBuilder', variant, mainImageContainer, useBundleBuilderLayers);
    // Validate inputs
    if (!variant || !mainImageContainer) {
        console.warn('Invalid inputs to initRenderImagesForBundleBuilder');
        return false;
    }

    const compositeData = window.Branch.compositeRenderImage.getCompositeData(variant.product_id);

    if(compositeData){
        // Parse product_thumbs_group_by into an array of normalized option names
        const relevantOptions = [];
        if (compositeData.product_thumbs_group_by) {
            const optionsList = compositeData.product_thumbs_group_by.split(',').map(opt => opt.trim());
            optionsList.forEach(opt => {
                relevantOptions.push(normalizeOptionName(opt));
            });
        }

        // Build variant options map from the variant object
        const variantOptions = {};

        // Extract options from variant - check different possible formats
        if (variant.options) {
            // If options is a string like "{ 'Seat Color': 'Black', 'Frame Color': 'Black', 'Size': 'Standard' }"
            if (typeof variant.options === 'string') {
                try {
                    // Clean up the string and parse it
                    const cleanedOptions = variant.options
                        .replace(/'/g, '"')  // Replace single quotes with double quotes
                        .replace(/(\w+)\s*:/g, '"$1":');  // Add quotes around keys if missing

                    const optionsObj = JSON.parse(cleanedOptions);

                    for (const [optionName, optionValue] of Object.entries(optionsObj)) {
                        const normalizedName = normalizeOptionName(optionName);
                        if (relevantOptions.includes(normalizedName)) {
                            variantOptions[normalizedName] = normalizeValue(optionValue);
                        }
                    }
                } catch (e) {
                    console.warn('Could not parse options string:', variant.options, e);
                }
            } else if (Array.isArray(variant.options)) {
                // Handle array format if needed
                variant.options.forEach((option, index) => {
                    // Map by index to standard option names if possible
                    if (variant[`option${index + 1}`]) {
                        const optionName = normalizeOptionName(`option${index + 1}`);
                        if (relevantOptions.includes(optionName)) {
                            variantOptions[optionName] = normalizeValue(option);
                        }
                    }
                });
            }
        }

        // Check for matching composite images
        const matchingImages = {
            firstLayer: [],
            secondLayer: []
        };

        // Determine which layers to use based on parameter
        const firstLayerKey = useBundleBuilderLayers ? 'first_layer_bundle_builder_images' : 'first_layer_images';
        const secondLayerKey = useBundleBuilderLayers ? 'second_layer_bundle_builder_images' : 'second_layer_images';

        // console.log('compositeData', compositeData)
        // console.log('variantOptions', variantOptions)
        // Check first layer images (either bundle builder or regular based on parameter)
        if (compositeData[firstLayerKey]) {
            compositeData[firstLayerKey].forEach((image) => {
                let allOptionsMatch = true;

                // Check if all image options match the variant options
                for (const [imgOptionName, imgOptionValue] of Object.entries(image.options)) {
                    const normalizedImgName = normalizeOptionName(imgOptionName);

                    // Skip this option if it's not in the relevant options list
                    if (!relevantOptions.includes(normalizedImgName)) {
                        continue;
                    }

                    const normalizedImgValue = normalizeValue(imgOptionValue);

                    if (variantOptions[normalizedImgName] !== normalizedImgValue) {
                        allOptionsMatch = false;
                        break;
                    }
                }

                if (allOptionsMatch) {
                    matchingImages.firstLayer.push(image);
                }
            });
        }

        // Check second layer images (either bundle builder or regular based on parameter)
        if (compositeData[secondLayerKey]) {
            compositeData[secondLayerKey].forEach((image) => {
                let allOptionsMatch = true;

                // Check if all image options match the variant options
                for (const [imgOptionName, imgOptionValue] of Object.entries(image.options)) {
                    const normalizedImgName = normalizeOptionName(imgOptionName);

                    // Skip this option if it's not in the relevant options list
                    if (!relevantOptions.includes(normalizedImgName)) {
                        continue;
                    }

                    const normalizedImgValue = normalizeValue(imgOptionValue);

                    if (variantOptions[normalizedImgName] !== normalizedImgValue) {
                        allOptionsMatch = false;
                        break;
                    }
                }

                if (allOptionsMatch) {
                    matchingImages.secondLayer.push(image);
                }
            });
        }

        // Also check alternate layer images as fallback if requested layers don't have matches
        const altFirstLayerKey = useBundleBuilderLayers ? 'first_layer_images' : 'first_layer_bundle_builder_images';
        if (matchingImages.firstLayer.length === 0 && compositeData[altFirstLayerKey]) {
            compositeData[altFirstLayerKey].forEach((image) => {
                let allOptionsMatch = true;

                // Check if all image options match the variant options
                for (const [imgOptionName, imgOptionValue] of Object.entries(image.options)) {
                    const normalizedImgName = normalizeOptionName(imgOptionName);

                    // Skip this option if it's not in the relevant options list
                    if (!relevantOptions.includes(normalizedImgName)) {
                        continue;
                    }

                    const normalizedImgValue = normalizeValue(imgOptionValue);

                    if (variantOptions[normalizedImgName] !== normalizedImgValue) {
                        allOptionsMatch = false;
                        break;
                    }
                }

                if (allOptionsMatch) {
                    matchingImages.firstLayer.push(image);
                }
            });
        }

        const altSecondLayerKey = useBundleBuilderLayers ? 'second_layer_images' : 'second_layer_bundle_builder_images';
        if (matchingImages.secondLayer.length === 0 && compositeData[altSecondLayerKey]) {
            compositeData[altSecondLayerKey].forEach((image) => {
                let allOptionsMatch = true;

                // Check if all image options match the variant options
                for (const [imgOptionName, imgOptionValue] of Object.entries(image.options)) {
                    const normalizedImgName = normalizeOptionName(imgOptionName);

                    // Skip this option if it's not in the relevant options list
                    if (!relevantOptions.includes(normalizedImgName)) {
                        continue;
                    }

                    const normalizedImgValue = normalizeValue(imgOptionValue);

                    if (variantOptions[normalizedImgName] !== normalizedImgValue) {
                        allOptionsMatch = false;
                        break;
                    }
                }

                if (allOptionsMatch) {
                    matchingImages.secondLayer.push(image);
                }
            });
        }

        // Check if we can create a composite image
        if (matchingImages.firstLayer.length > 0 && matchingImages.secondLayer.length > 0) {
            // console.log('✅ Can create composite image for variant:', variant.variant_id);
            // console.log('First Layer Matches:', matchingImages.firstLayer);
            // console.log('Second Layer Matches:', matchingImages.secondLayer);

            // Find the best match based on context
            let bestFirstLayer = matchingImages.firstLayer[0];
            let bestSecondLayer = matchingImages.secondLayer[0];

            if (useBundleBuilderLayers) {
                // For bundle builder main images - prefer overview
                for (const img of matchingImages.firstLayer) {
                    if (img.user_groups && img.user_groups.includes('overview')) {
                        bestFirstLayer = img;
                        break;
                    }
                }

                for (const img of matchingImages.secondLayer) {
                    if (img.user_groups && img.user_groups.includes('overview')) {
                        bestSecondLayer = img;
                        break;
                    }
                }
            } else {
                // For carousel thumbnails (from line 2073) - prefer front view
                for (const img of matchingImages.firstLayer) {
                    if (img.dimensions_overlay_view === 'front') {
                        bestFirstLayer = img;
                        break;
                    }
                }

                for (const img of matchingImages.secondLayer) {
                    if (img.dimensions_overlay_view === 'front') {
                        bestSecondLayer = img;
                        break;
                    }
                }

                // Log what was selected for debugging
                // console.log('Selected first layer:', bestFirstLayer.dimensions_overlay_view || 'no view specified');
                // console.log('Selected second layer:', bestSecondLayer.dimensions_overlay_view || 'no view specified');
            }

            // Create and insert the composite image into mainImageContainer
            if (mainImageContainer) {
                // Clear existing content
                mainImageContainer.innerHTML = '';

                // Create composite wrapper
                const compositeWrapper = document.createElement('div');
                compositeWrapper.className = 'bundle-builder-composite-wrapper';
                compositeWrapper.style.position = 'relative';
                compositeWrapper.style.width = '100%';
                compositeWrapper.style.height = '0%';
                compositeWrapper.style.paddingBottom = '100%';
                compositeWrapper.style.backgroundColor = '#F0F0F0';

                // Create the composite image HTML (larger srcset + eager for bundle-builder main images).
                compositeWrapper.innerHTML = window.Branch.compositeRenderImage.buildCompositeLayersHTML(bestFirstLayer, bestSecondLayer, {
                    widths: useBundleBuilderLayers ? [90, 180, 360, 540, 720, 900, 1080, 1280, 1440] : [90, 180, 360],
                    defaultWidth: useBundleBuilderLayers ? 540 : 180,
                    loading: useBundleBuilderLayers ? 'eager' : 'lazy',
                    extraClass: 'bundle-builder-composite-image',
                    altPrefix: `${variant.variant_title} - `,
                    style: 'position: absolute; display: block; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain;'
                });

                // Add the composite wrapper to the main image container
                mainImageContainer.appendChild(compositeWrapper);
                compositeImageReplacementFound = true;
            }
        } else {
            // No matching composite images for this variant
            compositeImageReplacementFound = false;
        }
    } else {
        // console.log('No composite render data found for product:', variant.product_id);
        compositeImageReplacementFound = false;
    }

    // Ensure we always return a boolean
    return compositeImageReplacementFound === true;
}

window.Branch.compositeRenderImage.initRenderImagesForProductTile = function(productCard, compositeRenderData, imageSrcSet, selectedVariant){
    // console.log('Checking composite image for product tile variant:', selectedVariant, imageSrcSet);

    // Check if this is being called from cart context
    const isCartContext = productCard && (
        productCard.classList.contains('one-third') ||
        productCard.classList.contains('small--two-fifths') ||
        productCard.closest('.cart-items') !== null ||
        productCard.closest('.items') !== null
    );

    // Parse the composite data
    let compositeData;
    try {
        compositeData = JSON.parse(compositeRenderData.textContent);
    } catch (error) {
        console.error('Error parsing composite render image data:', error);
        return false;
    }

    // Find the matching first/second layer images (prefer front view, then overview).
    const productId = compositeRenderData.getAttribute('data-id') || selectedVariant.product_id;
    const layers = window.Branch.compositeRenderImage.selectCompositeLayerPair(selectedVariant, productId, selectedVariant.options_names, {
        layerKeys: ['first_layer_images', 'second_layer_images'],
        prefer: [PREFER_FRONT, PREFER_OVERVIEW]
    });

    // Check if we can create a composite image
    if (layers) {
        createInsertCompositeWrapperForProductTile(productCard, layers.first, layers.second, selectedVariant, isCartContext, false, false);

        return {
            canCreate: true,
            firstLayer: layers.first,
            secondLayer: layers.second
        };
    } else {
        // console.log('❌ Cannot create composite image for variant:', selectedVariant.id);
        // console.log('First layer matches:', matchingImages.firstLayer.length);
        // console.log('Second layer matches:', matchingImages.secondLayer.length);

        // Restore regular image if no composite matches
        if (productCard) {
            // Find any existing composite wrapper and remove it
            const existingComposite = productCard.querySelector('.product-tile-composite-wrapper');
            if (existingComposite) {
                existingComposite.remove();
            }

            // Restore any tile images that were hidden while a composite was shown
            window.Branch.compositeRenderImage.restoreNonInvolvedTileImages(productCard);

            // Find the image container
            const imageContainer = productCard.querySelector('.product_tile_image_container, .product-tile__image-container, .product-card__image-container, .product-image-container');

            if (imageContainer) {
                // Find the original image that was hidden
                let originalImage = imageContainer.querySelector('img[data-hidden-for-composite="true"], img:not(.product-tile-composite-image)');

                if (originalImage) {
                    // Show the original image
                    originalImage.style.display = '';
                    originalImage.removeAttribute('data-hidden-for-composite');

                    // Update its srcset with the new value
                    if (imageSrcSet) {
                        originalImage.setAttribute('srcset', imageSrcSet);
                    }

                    // Update the src if we have featured_media
                    if (selectedVariant.featured_media?.src) {
                        originalImage.setAttribute('src', selectedVariant.featured_media.src);
                    }

                    // Update alt text
                    const altText = selectedVariant.featured_media?.alt || selectedVariant.options?.join(', ') || '';
                    originalImage.setAttribute('alt', altText);

                } else {
                    // If somehow no original image exists, create one
                    const imageSrc = selectedVariant.featured_media?.src || '';
                    const altText = selectedVariant.featured_media?.alt || selectedVariant.options?.join(', ') || '';

                    const newImage = document.createElement('img');
                    newImage.src = imageSrc;
                    newImage.srcset = imageSrcSet || '';
                    newImage.sizes = '(max-width: 600px) calc(100vw - 48px), (max-width: 840px) calc((100vw - 148px) /2), (max-width: 900px) calc((77.77vw - 20px) /2), (max-width: 1440px) calc((77.77vw - 40px) /3), calc((1120px - 40px) / 3)';
                    newImage.alt = altText;
                    newImage.width = 540;
                    newImage.height = 540;
                    newImage.loading = 'lazy';

                    imageContainer.appendChild(newImage);
                }

                // Update data-variant attribute on product-image element
                const productImage = productCard.querySelector('.product-image, .product_image');
                if (productImage) {
                    productImage.setAttribute('data-variant', selectedVariant.title || selectedVariant.options?.join(', ') || '');
                }
            }
        }

        return {
            canCreate: false,
            firstLayer: null,
            secondLayer: null
        };
    }
}

// While a composite is shown in a tile, hide any .product-image / .product-image-hover
// that is NOT the element containing the composite wrapper (the "involved" one).
window.Branch.compositeRenderImage.hideNonInvolvedTileImages = function(tileRoot, compositeWrapper){
    if (!tileRoot || !compositeWrapper) return;
    tileRoot.querySelectorAll('.product-image, .product-image-hover').forEach((el) => {
        if (!el.contains(compositeWrapper)) {
            el.setAttribute('data-hidden-for-composite-tile', 'true');
            el.style.display = 'none';
        }
    });
};

// Restore any tile images that were hidden while a composite was shown.
window.Branch.compositeRenderImage.restoreNonInvolvedTileImages = function(tileRoot){
    if (!tileRoot) return;
    tileRoot.querySelectorAll('[data-hidden-for-composite-tile="true"]').forEach((el) => {
        el.style.display = '';
        el.removeAttribute('data-hidden-for-composite-tile');
    });
};

function createInsertCompositeWrapperForProductTile(productCard, bestFirstLayer, bestSecondLayer, selectedVariant, isCartContext, static, lifestyleAsMainImage){
    // Create the composite image in the product card
    if (productCard) {
        const compositeWrapper = document.createElement('div');
        compositeWrapper.className = 'product-tile-composite-wrapper';
        compositeWrapper.style.position = 'relative';
        compositeWrapper.style.width = '100%';
        compositeWrapper.style.height = '0%';
        compositeWrapper.style.paddingBottom = '100%';
        compositeWrapper.style.backgroundColor = '#F0F0F0'


        // Create the composite image HTML
        compositeWrapper.innerHTML = window.Branch.compositeRenderImage.buildCompositeLayersHTML(bestFirstLayer, bestSecondLayer, {
            widths: [90, 180, 360, 540],
            defaultWidth: 90,
            loading: 'lazy',
            extraClass: 'product-tile-composite-image',
            altPrefix: `${selectedVariant.options ? selectedVariant.options.join(', ') : ''} - `,
            style: 'position: absolute; display: block; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain;'
        });

        // Copy the first .sale element from the product-image (only for product tiles, not cart items)
        if (!isCartContext) {
            const productImageElement = productCard.querySelector('.product-image');
            if (productImageElement) {
                const saleElement = productImageElement.querySelector('.product-image__swatch-container');
                if (saleElement) {
                    // Clone the sale element and append it to the composite wrapper
                    const clonedSale = saleElement.cloneNode(true);
                    compositeWrapper.appendChild(clonedSale);
                }
                // Feature collection sliders: the price-hydration badge (data-dc-text) sits outside the swatch container, copy it too
                const dcSaleElement = productCard.closest('.c-fcp-slider') && productImageElement.querySelector('.sale[data-dc-text="true"]');
                if (dcSaleElement) {
                    let swatchContainer = compositeWrapper.querySelector('.product-image__swatch-container');
                    if (!swatchContainer) {
                        swatchContainer = document.createElement('div');
                        swatchContainer.className = 'product-image__swatch-container';
                        compositeWrapper.appendChild(swatchContainer);
                    }
                    swatchContainer.appendChild(dcSaleElement.cloneNode(true));
                }
            }
        }

        // Find the image container
        let imageContainer;
        let targetContainer;

        if (isCartContext) {
            // For cart items, find the original image and its parent anchor
            const originalImage = productCard.querySelector('img');
            if (originalImage) {
                // Find the parent anchor element
                const parentAnchor = originalImage.closest('a');
                if (parentAnchor) {
                    // Use the anchor as the target container
                    targetContainer = parentAnchor;
                    imageContainer = parentAnchor;
                } else {
                    // Fallback to the productCard if no anchor found
                    targetContainer = productCard;
                    imageContainer = productCard;
                }

                // Hide the original image
                originalImage.style.display = 'none';
                originalImage.setAttribute('data-hidden-for-composite', 'true');
            }
        } else {
            // For product tiles, find the image container within the product card
            imageContainer = productCard.querySelector('.product_tile_image_container, .product-tile__image-container, .product-card__image-container, .product-image-container');
            targetContainer = imageContainer;

            if (imageContainer && !lifestyleAsMainImage) {
                // Hide the original image
                const originalImage = imageContainer.querySelector('img:not(.product-tile-composite-image)');
                if (originalImage) {
                    originalImage.style.display = 'none';
                    originalImage.setAttribute('data-hidden-for-composite', 'true');
                }
            }
        }

        if(static && lifestyleAsMainImage){
            const productImageElement = productCard.querySelector('.product-image');
            let productImageHover = productImageElement?.querySelector('.product-image-hover');
            productImageHover.innerHTML = '';
            productImageHover.appendChild(compositeWrapper);
        } else {
        if (targetContainer) {
            // Remove any existing composite wrapper first
            const existingComposite = targetContainer.querySelector('.product-tile-composite-wrapper');
            if (existingComposite) {
                existingComposite.remove();
            }

            // Add the new composite wrapper
            targetContainer.appendChild(compositeWrapper);

            // Add hover functionality to change background image
            const productImageElement = productCard.querySelector('.product-image');
            const productImageHover = productImageElement?.querySelector('.product-image-hover img');
            // Feature collection sliders: match .product-image and skip the hover swap when hover is disabled
            const hoverDisabled = productCard.closest('.c-fcp-slider') && productImageElement?.getAttribute('data-image-on-hover-type') === 'none';

            if (productImageHover && !hoverDisabled) {
                // Get the hover image URL from srcset (540w version)
                const srcset = productImageHover.getAttribute('srcset');
                let hoverImageSrc = null;

                if (srcset) {
                    // Parse srcset to find the 540w URL
                    const srcsetParts = srcset.split(',').map(s => s.trim());
                    const match540 = srcsetParts.find(part => part.includes('540w'));

                    if (match540) {
                        // Extract the URL from "url 540w" format
                        hoverImageSrc = match540.split(' ')[0];
                    }
                }

                // Fallback to src if no 540w found in srcset
                if (!hoverImageSrc) {
                    hoverImageSrc = productImageHover.getAttribute('src');
                }
                if (hoverImageSrc) {
                    // Add hover event listeners to the composite wrapper
                    compositeWrapper.addEventListener('mouseenter', function() {
                        this.style.backgroundImage = `url(${hoverImageSrc})`;
                        this.style.backgroundSize = 'contain';
                        this.style.backgroundPosition = 'center';
                        this.style.backgroundRepeat = 'no-repeat';
                    });

                    compositeWrapper.addEventListener('mouseleave', function() {
                        this.style.backgroundImage = '';
                    });
                }
            }

            // Update data-variant attribute on product-image element
            const productImage = productCard.querySelector('.product-image, .product_image');
            if (productImage) {
                productImage.setAttribute('data-variant', selectedVariant.title || selectedVariant.options?.join(', ') || '');
            }

            // Hide any non-involved .product-image / .product-image-hover while the composite is shown
            if (!isCartContext) {
                window.Branch.compositeRenderImage.hideNonInvolvedTileImages(productCard, compositeWrapper);
            }
        } else {
            // console.log('Could not find image container in product card');
        }
        }
        
    }
}

window.Branch.compositeRenderImage.initRenderImagesForProduct = function(type, isQuickView, product){
  return new Promise((resolve) => {
    // Check if script element exists with the required class and data-id
    const scriptElement = document.querySelector(`script.composite-render-image-merchandising-data[data-id="${product.id}"]`);

    if (!scriptElement) {
      // Script element doesn't exist, exit early
    //   console.log('No composite render image data found for product:', product?.id);
      resolve();
      return;
    }

    // Script element exists, continue with composite render image logic
    // console.log('Composite render image data found for product:', product?.id);

    // Extract and parse the data from the script element
    let compositeData;
    try {
      compositeData = JSON.parse(scriptElement.textContent);
    } catch (error) {
      console.error('Error parsing composite render image data:', error);
      resolve();
      return;
    }

    // console.log('Extracted composite data:', compositeData);

    // Parse product_thumbs_group_by into an array of normalized option names
    const relevantOptions = [];
    if (compositeData.product_thumbs_group_by) {
      const optionsList = compositeData.product_thumbs_group_by.split(',').map(opt => opt.trim());
      optionsList.forEach(opt => {
        relevantOptions.push(normalizeOptionName(opt));
      });
    }


    // Loop through all product variants
    product.variants.forEach((variant) => {

      // Build a map of normalized option names to values for this variant
      // Only include options that are in the relevantOptions list
      const variantOptions = {};
      product.options.forEach((optionName, index) => {
        const normalizedName = normalizeOptionName(optionName);

        // Only include this option if it's in the relevantOptions list
        if (relevantOptions.includes(normalizedName)) {
          const optionValue = variant[`option${index + 1}`];
          if (optionValue) {
            variantOptions[normalizedName] = normalizeValue(optionValue);
          }
        }
      });

      // Check for matching composite images
      const matchingImages = {
        firstLayer: [],
        secondLayer: []
      };

      // Check first layer images
      if (compositeData.first_layer_images) {
        compositeData.first_layer_images.forEach((image) => {
          let allOptionsMatch = true;

          // Check if all image options match the variant options
          // Only check options that are in the relevantOptions list
          for (const [imgOptionName, imgOptionValue] of Object.entries(image.options)) {
            const normalizedImgName = normalizeOptionName(imgOptionName);

            // Skip this option if it's not in the relevant options list
            if (!relevantOptions.includes(normalizedImgName)) {
              continue;
            }

            const normalizedImgValue = normalizeValue(imgOptionValue);

            if (variantOptions[normalizedImgName] !== normalizedImgValue) {
              allOptionsMatch = false;
              break;
            }
          }

          if (allOptionsMatch) {
            matchingImages.firstLayer.push(image);
          }
        });
      }

      // Check second layer images
      if (compositeData.second_layer_images) {
        compositeData.second_layer_images.forEach((image) => {
          let allOptionsMatch = true;

          // Check if all image options match the variant options
          // Only check options that are in the relevantOptions list
          for (const [imgOptionName, imgOptionValue] of Object.entries(image.options)) {
            const normalizedImgName = normalizeOptionName(imgOptionName);

            // Skip this option if it's not in the relevant options list
            if (!relevantOptions.includes(normalizedImgName)) {
              continue;
            }

            const normalizedImgValue = normalizeValue(imgOptionValue);

            if (variantOptions[normalizedImgName] !== normalizedImgValue) {
              allOptionsMatch = false;
              break;
            }
          }

          if (allOptionsMatch) {
            matchingImages.secondLayer.push(image);
          }
        });
      }

      // Process matches if found
      if (matchingImages.firstLayer.length > 0 && matchingImages.secondLayer.length > 0) {
        // console.log(`✅ MATCH FOUND for variant: ${variant.title}`);
        // console.log('Variant ID:', variant.id);
        // console.log('Variant Options:', variant.options);
        // console.log('First Layer Matches:', matchingImages.firstLayer);
        // console.log('Second Layer Matches:', matchingImages.secondLayer);
        // console.log('---');

        // Collect all valid composite combinations
        const compositeCombinations = [];

        matchingImages.firstLayer.forEach((firstLayerImage) => {
          matchingImages.secondLayer.forEach((secondLayerImage) => {
            // Skip if dimensions overlay views don't match between layers
            // (unless one is missing, then use the other)
            const firstDimensions = firstLayerImage.dimensions_overlay_view || '';
            const secondDimensions = secondLayerImage.dimensions_overlay_view || '';

            // If both have dimensions values and they don't match, skip this combination
            if (firstDimensions && secondDimensions && firstDimensions !== secondDimensions) {
              return;
            }

            // Add valid combination to array
            compositeCombinations.push({
              firstLayerImage,
              secondLayerImage,
              dimensionsOverlayView: firstDimensions || secondDimensions || ''
            });
          });
        });

        // Sort combinations so that 'front' views come before other views
        compositeCombinations.sort((a, b) => {
          if (a.dimensionsOverlayView === 'front' && b.dimensionsOverlayView !== 'front') return -1;
          if (a.dimensionsOverlayView !== 'front' && b.dimensionsOverlayView === 'front') return 1;
          return 0;
        });

        // Process sorted combinations
        compositeCombinations.forEach((combination) => {
          const { firstLayerImage, secondLayerImage, dimensionsOverlayView } = combination;

          // Get the gallery container
          const galleryContainer = document.querySelector(`.main-product__item[data-quick-view="${isQuickView}"] .main-product-gallery__inner-container`);

          if (!galleryContainer) {
            return;
          }

          // Build data attributes for variant options to check for existing match
          let optionAttributes = '';
          const optionSelectors = [];
          product.options.forEach((optionName, index) => {
            const normalizedName = normalizeOptionName(optionName);
            const optionValue = variant[`option${index + 1}`];
            if (optionValue) {
              const normalizedValue = normalizeValue(optionValue);
              optionAttributes += ` data-${normalizedName}="_${normalizedValue}_"`;

              // Only add to selectors if this option is in the relevant options list
              if (relevantOptions.includes(normalizedName)) {
                optionSelectors.push(`[data-${normalizedName}="_${normalizedValue}_"]`);
              }
            }
          });

            // console.log('Looking for existing match with selectors:', optionSelectors);

          // Get user groups (prioritize first layer, fallback to second layer)
          const userGroups = firstLayerImage.user_groups || secondLayerImage.user_groups || '';

          // Every other gallery consumer reads data-dimensions-overlay-view in the
          // underscore-wrapped form Liquid emits (_front_, _none_ — see
          // snippets/_product_gallery_grouping_data.liquid), so wrap it here too. No view
          // means _none_, matching the Liquid default.
          const normalizedDimensionsView = normalizeValue(dimensionsOverlayView);
          const dimensionsOverlayViewAttr = normalizedDimensionsView ? `_${normalizedDimensionsView}_` : '_none_';

          // Check if an existing image matches all the relevant options and dimensions overlay view
          let existingMatch = null;
          if (optionSelectors.length > 0 || dimensionsOverlayView) {
            // Build selector for dimensions overlay view if it exists (use hyphens in attribute name)
            const dimensionsSelector = normalizedDimensionsView ? `[data-dimensions-overlay-view="_${normalizedDimensionsView}_"]` : '';

            // Find existing image that matches all criteria
            const fullSelector = `.main-product__item[data-quick-view="${isQuickView}"] .main-product-gallery__inner-container .main-product-gallery__item${optionSelectors.join('')}${dimensionsSelector}`;

            existingMatch = document.querySelector(fullSelector);

            if (existingMatch) {
              // console.log(`Found existing matching image for variant ${variant.id} with dimensions_overlay_view="${dimensionsOverlayView}", will replace it`);
            } else {
              const allGalleryItems = document.querySelectorAll(`.main-product__item[data-quick-view="${isQuickView}"] .main-product-gallery__inner-container .main-product-gallery__item`);

              // Log first few items to see their attributes
              // allGalleryItems.forEach((item, idx) => {
              //   if (idx < 3) {
              //     console.log(`Item ${idx} attributes:`, {
              //       'top-color': item.getAttribute('data-top-color'),
              //       'leg-color': item.getAttribute('data-leg-color'),
              //       'desk-size': item.getAttribute('data-desk-size'),
              //       'dimensions-overlay-view': item.getAttribute('data-dimensions-overlay-view')
              //     });
              //   }
              // });
            }
          }

       let newIndex;
          let insertionPoint = null;
          const galleryItemsForIndex = galleryContainer.querySelectorAll('.main-product-gallery__item');
          const maxExistingIndex = Array.from(galleryItemsForIndex).reduce((max, item) => {
            const di = parseInt(item.getAttribute('data-index'), 10);
            return (!isNaN(di) && di > max) ? di : max;
          }, -1);
          if (existingMatch) {
            newIndex = existingMatch.getAttribute('data-index');
            insertionPoint = existingMatch;
            const reuseCollides = Array.from(galleryItemsForIndex).some(item => item !== existingMatch && item.getAttribute('data-index') === String(newIndex));
            if (reuseCollides) newIndex = maxExistingIndex + 1;
          } else {
            newIndex = maxExistingIndex + 1;
          }

          // Get media_id if available
          const mediaId = firstLayerImage.media_id || secondLayerImage.media_id || '';
          const mediaIdAttr = mediaId ? `data-media-id="${mediaId}"` : '';

          // Create high-res URLs for zoom by adding width parameter
          const firstLayerZoomUrl = `${firstLayerImage.url}&width=1800`;
          const secondLayerZoomUrl = `${secondLayerImage.url}&width=1800`;

          // Build the composite image HTML with background images for zoom
          const compositeImageHTML = `
              <div class="main-product-gallery__item swiper-slide"
                   data-index="${newIndex}"
                   ${optionAttributes}
                   data-user-groups="${userGroups}"
                   data-dimensions-overlay-view="${dimensionsOverlayViewAttr}"
                   ${mediaIdAttr}
                   data-composite-render="true"
                   data-variant-id="${variant.id}"
                   data-composite-first-layer="${firstLayerZoomUrl}"
                   data-composite-second-layer="${secondLayerZoomUrl}">
                <div class="main-product-gallery__image-container swiper-zoom-container">
                  <div class="main-product-gallery__image-inner-container"
                       data-composite-bg-first="${firstLayerZoomUrl}"
                       data-composite-bg-second="${secondLayerZoomUrl}">
                    <div class="main-product-gallery__bg-image"></div>
                    <div class="composite-image-wrapper">
                      <img class="composite-layer-second main-product__main-product-image"
                           width="540"
                           height="540"
                           src="${secondLayerImage.url}&width=90"
                           data-lazy-srcset="${secondLayerImage.url}&width=90 90w,${secondLayerImage.url}&width=180 180w,${secondLayerImage.url}&width=360 360w,${secondLayerImage.url}&width=540 540w,${secondLayerImage.url}&width=720 720w,${secondLayerImage.url}&width=900 900w,${secondLayerImage.url}&width=1080 1080w,${secondLayerImage.url}&width=1280 1280w"
                           sizes="(max-width: 600px) 100vw, (max-width: 900px) calc(100vw - 48px), (max-width: 1167px) calc(((100vw - 48px)) - 490px), (max-width: 1259px) calc(((100vw - 84px)) * 0.55), 648px"
                           alt="${variant.title} - Second Layer"
                           loading="lazy">
                      <img class="composite-layer-first main-product__main-product-image"
                           width="540"
                           height="540"
                           src="${firstLayerImage.url}&width=90"
                           data-lazy-srcset="${firstLayerImage.url}&width=90 90w,${firstLayerImage.url}&width=180 180w,${firstLayerImage.url}&width=360 360w,${firstLayerImage.url}&width=540 540w,${firstLayerImage.url}&width=720 720w,${firstLayerImage.url}&width=900 900w,${firstLayerImage.url}&width=1080 1080w,${firstLayerImage.url}&width=1280 1280w"
                           sizes="(max-width: 600px) 100vw, (max-width: 900px) calc(100vw - 48px), (max-width: 1167px) calc(((100vw - 48px)) - 490px), (max-width: 1259px) calc(((100vw - 84px)) * 0.55), 648px"
                           alt="${variant.title} - First Layer"
                           loading="lazy">
                    </div>
                    <div class="sale"></div>
                  </div>
                </div>
              </div>
          `;

          // Either replace existing image or append new one
          if (existingMatch) {
            // Replace the existing image with the composite
            existingMatch.insertAdjacentHTML('afterend', compositeImageHTML);
            existingMatch.remove();
          //   console.log(`Replaced existing image with composite for variant ${variant.id} at index ${newIndex}, dimensions_overlay_view="${dimensionsOverlayView}"`);
          } else {
            // Append the composite image to the gallery
            galleryContainer.insertAdjacentHTML('beforeend', compositeImageHTML);
          //   console.log(`Added new composite image for variant ${variant.id} at index ${newIndex}, dimensions_overlay_view="${dimensionsOverlayView}"`);
          }
        });
      }
    });

    // Generate CSS for composite image zoom backgrounds
    window.Branch.compositeRenderImage.generateCompositeZoomCSS(isQuickView, product.id);

    resolve();
  });
}

// Generate CSS for composite image zoom backgrounds
window.Branch.compositeRenderImage.generateCompositeZoomCSS = function(isQuickView, productId) {
  // Get or create the style element
  let styleElement = document.querySelector('#composite-zoom-styles');
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = 'composite-zoom-styles';
    document.head.appendChild(styleElement);
  }

  let dimensionsOverlayDataObject = document.querySelector(`#dimensions-overlay-values--${productId}`);
  let dimensionsOverlayData = null;
  if(dimensionsOverlayDataObject){
    dimensionsOverlayData = JSON.parse(dimensionsOverlayDataObject.innerText);
  }


  // Start building CSS rules
    let cssRules = '';
//   cssRules = `
//     /* Composite image zoom styles */

//     /* Hide regular images during zoom for composite items */
//     .main-product-gallery[data-zoom="true"] .main-product-gallery__item[data-composite-render="true"] .main-product-gallery__image-container[data-zoom-activated="true"][data-hide-image="true"] .composite-image-wrapper {
//       opacity: 0;
//     }
//   `;

  // Find all composite images and generate CSS rules for their zoom backgrounds
  const quickViewValue = isQuickView ? 'true' : 'false';
  const compositeImages = document.querySelectorAll(`.main-product__item[data-quick-view="${quickViewValue}"] .main-product-gallery__item[data-composite-render="true"]`);

  compositeImages.forEach((item) => {
    const index = item.getAttribute('data-index');
    const innerContainer = item.querySelector('.main-product-gallery__image-inner-container');

    if (innerContainer) {
      const firstLayerUrl = innerContainer.getAttribute('data-composite-bg-first');
      const secondLayerUrl = innerContainer.getAttribute('data-composite-bg-second');

      if (firstLayerUrl && secondLayerUrl && index) {
        // Generate CSS rule for this specific composite image
        cssRules += `
                    .main-product-gallery__zoom-container .main-product-gallery__item[data-index="${index}"] .main-product-gallery__image-container[data-zoom-activated="true"] .main-product-gallery__image-inner-container {
                        background-image: url(${firstLayerUrl}), url(${secondLayerUrl}) !important;
                        background-color: var(--render-bg-color); 
                    }`;
        if(innerContainer.closest('.main-product-gallery__item') && innerContainer.closest('.main-product-gallery__item').getAttribute('data-dimensions-overlay-view') !== '_none_' && dimensionsOverlayData !== null){
            let imageDimensionsOverlayUrl = window.Branch.productGallery._fetchDimensionsOverlayImgUrl(dimensionsOverlayData, innerContainer);
            cssRules += `
                    .main-product-gallery__zoom-container[data-dimensions-overlay-active="true"] .main-product-gallery__item[data-index="${index}"] .main-product-gallery__image-container[data-zoom-activated="true"] .main-product-gallery__image-inner-container {
                        background-image: url(${firstLayerUrl}), url(${secondLayerUrl}), url(${imageDimensionsOverlayUrl}) !important;
                    }`;
        }
      }
    }
  });

  // Update the style element with all the CSS rules
  styleElement.innerHTML = cssRules;
}

window.Branch.compositeRenderImage.renderConstituentCompositeSlots = function(root){
    root = root || document;
    const slots = root.querySelectorAll('.pre-set-bundle-image__product-overlay[data-composite-constituent][data-composite-variant]');
    slots.forEach((slot) => {
        if (slot.children.length > 0) return; // already rendered
        const productId = parseInt(slot.getAttribute('data-composite-constituent'), 10);
        if (!productId) return;
        let variant, optionNames = null;
        try { variant = JSON.parse(slot.getAttribute('data-composite-variant')); } catch (e) { return; }
        try {
            const rawOpts = slot.getAttribute('data-composite-option-names');
            if (rawOpts) optionNames = JSON.parse(rawOpts);
        } catch (e) { optionNames = null; }
        try {
            window.Branch.compositeRenderImage.renderBundleConstituentComposite(variant, productId, optionNames, slot);
        } catch (e) {
            console.error('Error rendering bundle constituent composite slot:', e);
        }
    });
};

// Sweep on initial load, then watch for tiles added later (collection grids / AJAX / lazy render).
(function(){
    const sweep = () => window.Branch.compositeRenderImage.renderConstituentCompositeSlots(document);
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', sweep);
    } else {
        sweep();
    }
    window.addEventListener('load', sweep);

    let scheduled = false;
    const scheduleSweep = () => {
        if (scheduled) return;
        scheduled = true;
        const run = () => { scheduled = false; sweep(); };
        if (window.requestAnimationFrame) { window.requestAnimationFrame(run); } else { setTimeout(run, 16); }
    };
    const startObserver = () => {
        if (!document.body) return;
        const observer = new MutationObserver((mutations) => {
            for (const m of mutations) {
                if (m.addedNodes && m.addedNodes.length) { scheduleSweep(); return; }
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) { startObserver(); } else { document.addEventListener('DOMContentLoaded', startObserver); }
})();